import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import pedirSenhaAdmin from '../components/common/pedirSenhaAdmin';
import styles from './Page.module.css';

const FORMAS_PAGAMENTO = [
  { value: 'dinheiro', label: 'Dinheiro' },
  { value: 'cartao_credito', label: 'Cartão de Crédito' },
  { value: 'cartao_debito', label: 'Cartão de Débito' },
  { value: 'pix', label: 'PIX' },
];

export default function Vendas() {
  const qc = useQueryClient();
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ forma_pagamento: 'pix', itens: [{ produto_id: '', quantidade: 1 }] });

  const { data: vendas = [], isLoading } = useQuery({
    queryKey: ['vendas'],
    queryFn: () => api.get('/vendas').then((r) => r.data),
  });

  const criar = useMutation({
    mutationFn: (dados) => api.post('/vendas', dados),
    onSuccess: () => { qc.invalidateQueries(['vendas']); setModal(false); },
  });

  const cancelar = useMutation({
    mutationFn: ({ id, senha, motivo }) => api.patch(`/vendas/${id}/cancelar`, { senha_admin: senha, motivo }),
    onSuccess: () => qc.invalidateQueries(['vendas']),
    // Se a venda tiver NF-e/NFC-e autorizada, o backend exige motivo com
    // pelo menos 15 caracteres (vira a justificativa da SEFAZ) em vez dos
    // 5 mínimos daqui — a mensagem de erro já explica isso, só mostra.
    onError: (err) => alert(err.response?.data?.erro || 'Erro ao cancelar venda.'),
  });

  const formatBRL = (v) => Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const formatData = (d) => new Date(d).toLocaleString('pt-BR');

  const addItem = () => setForm((f) => ({ ...f, itens: [...f.itens, { produto_id: '', quantidade: 1 }] }));
  const removeItem = (i) => setForm((f) => ({ ...f, itens: f.itens.filter((_, idx) => idx !== i) }));
  const updateItem = (i, field, value) => setForm((f) => ({
    ...f,
    itens: f.itens.map((item, idx) => idx === i ? { ...item, [field]: value } : item),
  }));

  return (
    <div>
      <div className={styles.topbar}>
        <h2 className={styles.titulo}>Vendas</h2>
        <button className={styles.btnPrimario} onClick={() => setModal(true)}>+ Nova Venda</button>
      </div>

      {isLoading ? <p>Carregando...</p> : (
        <div className={styles.tabela}>
          <table>
            <thead>
              <tr><th>#</th><th>Data</th><th>Vendedor</th><th>Cliente</th><th>Pagamento</th><th>Total</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {vendas.map((v) => (
                <tr key={v.id}>
                  <td>#{v.id}</td>
                  <td>{formatData(v.criado_em)}</td>
                  <td>{v.vendedor}</td>
                  <td>{v.cliente || 'Consumidor'}</td>
                  <td>{FORMAS_PAGAMENTO.find((f) => f.value === v.forma_pagamento)?.label}</td>
                  <td><strong>{formatBRL(v.total)}</strong></td>
                  <td><span className={v.status === 'cancelada' ? styles.estoqueAlerta : ''}>{v.status}</span></td>
                  <td>
                    {v.status === 'concluida' && (
                      <button className={styles.btnPerigo} onClick={async () => {
                        const motivo = window.prompt('Cancelar venda — motivo do cancelamento (mínimo 5 caracteres):');
                        if (motivo === null) return;
                        if (motivo.trim().length < 5) { alert('Motivo deve ter no mínimo 5 caracteres.'); return; }
                        const senha = await pedirSenhaAdmin();
                        if (senha === null) return;
                        cancelar.mutate({ id: v.id, senha, motivo: motivo.trim() });
                      }}>Cancelar</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div className={styles.overlay}>
          <div className={styles.modal}>
            <h3>Nova Venda</h3>
            <form onSubmit={(e) => { e.preventDefault(); criar.mutate({ ...form, itens: form.itens.map((i) => ({ produto_id: parseInt(i.produto_id), quantidade: parseInt(i.quantidade) })) }); }}>
              <label className={styles.label}>
                Forma de Pagamento *
                <select className={styles.input} value={form.forma_pagamento} onChange={(e) => setForm({ ...form, forma_pagamento: e.target.value })}>
                  {FORMAS_PAGAMENTO.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </label>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <strong style={{ fontSize: '0.9rem' }}>Itens</strong>
                  <button type="button" className={styles.btnSecundario} onClick={addItem}>+ Item</button>
                </div>
                {form.itens.map((item, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 80px auto', gap: 6, marginBottom: 6 }}>
                    <input className={styles.input} placeholder="ID do Produto" type="number" required value={item.produto_id} onChange={(e) => updateItem(i, 'produto_id', e.target.value)} />
                    <input className={styles.input} placeholder="Qtd" type="number" min="1" required value={item.quantidade} onChange={(e) => updateItem(i, 'quantidade', e.target.value)} />
                    {form.itens.length > 1 && <button type="button" className={styles.btnPerigo} onClick={() => removeItem(i)}>×</button>}
                  </div>
                ))}
              </div>

              <div className={styles.modalAcoes}>
                <button type="button" className={styles.btnSecundario} onClick={() => setModal(false)}>Cancelar</button>
                <button type="submit" className={styles.btnPrimario} disabled={criar.isPending}>Confirmar Venda</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
